// src/features/analytics/viewerMix.ts
//
// Pure transforms for the new-vs-returning viewer mix (metrics expansion).
// playback/daily points MAY carry sessionsNew/sessionsReturning — both arrive
// together, and both are absent for days not yet materialized (typically
// today). A point "carries the mix" only when both fields are present; days
// without the fields are excluded rather than fabricated as zeros. The split
// is cookie-based, so callers must pair any chart with the honesty caveat
// (private-mode and cookie-blocking browsers count as new).

import { SECONDS_PER_DAY } from '@/shared/utils/utcRange';
import type { PlaybackDailyPoint } from './types';

/** One measured (or in-window gap-filled) UTC day of the mix series. */
export interface ViewerMixDay {
  day: number;
  sessionsNew: number;
  sessionsReturning: number;
}

function measuredDays(points: PlaybackDailyPoint[]): ViewerMixDay[] {
  return points
    .filter((p) => p.sessionsNew !== undefined && p.sessionsReturning !== undefined)
    .map((p) => ({
      day: p.day,
      sessionsNew: p.sessionsNew as number,
      sessionsReturning: p.sessionsReturning as number,
    }))
    .sort((a, b) => a.day - b.day);
}

/**
 * Returning share of sessions (0..1) summed over the days that carry the mix
 * fields. Null when no point carries them or the measured total is zero — an
 * honest "no data" for the stat card, never a fabricated 0%.
 */
export function returningShare(points: PlaybackDailyPoint[]): number | null {
  const days = measuredDays(points);
  const total = days.reduce((sum, d) => sum + d.sessionsNew + d.sessionsReturning, 0);
  if (days.length === 0 || total === 0) return null;
  const returning = days.reduce((sum, d) => sum + d.sessionsReturning, 0);
  return returning / total;
}

/**
 * Stacked-chart series: the measured days, zero-filled ONLY between the first
 * and last day carrying the fields (axis continuity — inside the measured
 * window an omitted day means no sessions, same rationale as fillDailyGaps).
 * Days outside the window (pre-rollout or not yet materialized) are never
 * fabricated. Empty when nothing is measured.
 */
export function viewerMixSeries(points: PlaybackDailyPoint[]): ViewerMixDay[] {
  const days = measuredDays(points);
  if (days.length === 0) return [];
  const byDay = new Map(days.map((d) => [d.day, d]));
  const out: ViewerMixDay[] = [];
  for (let day = days[0].day; day <= days[days.length - 1].day; day += SECONDS_PER_DAY) {
    out.push(byDay.get(day) ?? { day, sessionsNew: 0, sessionsReturning: 0 });
  }
  return out;
}
